import React, { useState } from 'react';
import { ShieldCheck, Clock, Info, CheckCircle2, Camera, Trophy, Sparkles } from 'lucide-react';

interface CourtStatusBadgeTooltipProps {
  status?: 'verified' | 'pending' | string;
  className?: string;
  showText?: boolean;
}

export const CourtStatusBadgeTooltip: React.FC<CourtStatusBadgeTooltipProps> = ({
  status = 'verified',
  className = '',
  showText = true,
}) => {
  const isVerified = status === 'verified';
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* BADGE TRIGGER BUTTON */}
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black italic uppercase cursor-help transition-all duration-200 border shadow-sm ${
          isVerified
            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/30 hover:border-emerald-400'
            : 'bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30 hover:border-amber-400'
        }`}
      >
        {isVerified ? (
          <>
            <ShieldCheck className="w-3 h-3 text-emerald-400 shrink-0" />
            {showText && <span>Verified</span>}
          </>
        ) : (
          <>
            <Clock className="w-3 h-3 text-amber-300 shrink-0" />
            {showText && <span>Pending</span>}
          </>
        )}
      </span>

      {/* HOVER-ACTIVATED TOOLTIP CARD */}
      {isHovered && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-72 p-3.5 bg-slate-900/98 text-white rounded-2xl border border-lime-400/60 shadow-[0_10px_30px_rgba(0,0,0,0.8)] z-50 pointer-events-none text-left animate-fadeIn backdrop-blur-xl">
          {/* Arrow Tail */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-8 border-transparent border-t-slate-900" />

          <div className="space-y-2.5">
            {/* Header */}
            <div className="flex items-center space-x-2 border-b border-white/10 pb-2">
              {isVerified ? (
                <div className="p-1.5 bg-emerald-500 text-black rounded-xl shrink-0 shadow">
                  <ShieldCheck className="w-4 h-4 stroke-[2.5]" />
                </div>
              ) : (
                <div className="p-1.5 bg-amber-400 text-black rounded-xl shrink-0 shadow">
                  <Clock className="w-4 h-4 stroke-[2.5]" />
                </div>
              )}

              <div>
                <span className="text-[9px] font-black uppercase text-lime-400 tracking-wider block">
                  COURT BADGE STATUS
                </span>
                <h5 className="text-xs font-black italic uppercase text-white leading-tight">
                  {isVerified ? 'Official Verified Court 🛡️' : 'Pending Scout Review ⏳'}
                </h5>
              </div>
            </div>

            {/* Validation Criteria */}
            <div className="space-y-1">
              <span className="text-[10px] font-extrabold uppercase text-indigo-300 flex items-center gap-1">
                <Info className="w-3 h-3 text-lime-400" />
                Validation Criteria:
              </span>
              <p className="text-[10px] text-slate-200 leading-relaxed bg-black/40 p-2 rounded-xl border border-white/5">
                {isVerified
                  ? 'Inspected by Playground League scouts for official rim height, durable surface, safety netting, geofenced GPS bounds, and evening lighting.'
                  : 'Submitted by local community players. Awaiting official scout inspection and 3 verified game check-ins.'}
              </p>
            </div>

            {/* How Players Can Earn Verified Status */}
            <div className="space-y-1 pt-1 border-t border-white/10">
              <span className="text-[10px] font-black uppercase text-amber-300 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-400" />
                How to Earn 'Verified' Status for Parks:
              </span>
              <ul className="text-[10px] text-slate-300 space-y-1 bg-indigo-950/80 p-2 rounded-xl border border-white/5">
                <li className="flex items-start space-x-1.5">
                  <Camera className="w-3 h-3 text-cyan-400 shrink-0 mt-0.5" />
                  <span>Submit 3 clear photos of court & rim condition.</span>
                </li>
                <li className="flex items-start space-x-1.5">
                  <Trophy className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />
                  <span>Host 3+ successful pickup games at this park.</span>
                </li>
                <li className="flex items-start space-x-1.5">
                  <CheckCircle2 className="w-3 h-3 text-lime-400 shrink-0 mt-0.5" />
                  <span>Gather 10+ athlete check-ins or request scout review.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
